import { Button, Card, Box, Container, IconButton, Table, TableBody, TableContainer, TableHead, TableRow, MenuItem, TableCell } from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { enqueueSnackbar } from "notistack";
import axios, { endpoints } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { EldLandmark, Organization } from "src/models";
import useModelData, { replaceById, deleteById } from "src/hooks/use-model-data";
import Iconify from "src/components/iconify/iconify";
import { DisplayPageData } from "src/components/display-page-data";
import useModelSavingData from "src/hooks/use-model-saving-data";

import SimpleDialog from "src/components/simple-dialog";
import PageHeader from "src/components/page-header";
import NoneFound from "src/components/none-found";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import { TruxTableCell } from "src/components/trux-table-cell";
import { useRouter } from "src/routes/hooks";
import { useSearch } from "src/hooks/use-search";
import { SearchCard } from "src/components/search-card";
import DisplayErrors from "src/components/display-errors";
import { formatAddress } from "src/utils/address-utils";
import TruxSimpleSelectField from "src/components/inputs/trux-simple-select-field";
import NoneFoundReport from "src/components/none-found-report";
import TruxSimpleCheckboxField from "src/components/inputs/trux-simple-checkbox-field";
import { formatDateForPreview } from "src/utils/date-utils";

type TrailersPerTruckLine = {
  trailer: string,
  vehicleUnitNumber: string,
  latestUseDate: string,
  eldDriverFirstName: string,
  eldDriverLastName: string,
}

type TrailersPerTruckLineListProps = {
  trailersPerTruckLines?: TrailersPerTruckLine[],
}

const TrailersPerTruckLineList = memo(({ trailersPerTruckLines }: TrailersPerTruckLineListProps) => {
  const { sortedList, sortData } = useSort(trailersPerTruckLines);
  const router = useRouter();

  const {
    searchText,
    setSearchText,
    filteredList,
  } = useSearch(sortedList, ["vehicleUnitNumber", "trailer", "eldDriverFirstName", "eldDriverLastName"]);

  if (!sortedList) {
    return null;
  }

  return <div>
    <SearchCard searchText={searchText} setSearchText={setSearchText} searchingFor="Trailer Assignments"/>

    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table>
          <TableHead>
            <TableRow>
              <SortableHeader sortKey="trailer" sortData={sortData}>
                Trailer
              </SortableHeader>

              <SortableHeader sortKey="vehicleUnitNumber" sortData={sortData}>
                Truck
              </SortableHeader>

              <SortableHeader sortKey="latestUseDate" sortData={sortData} align="center">
                Last Used Date
              </SortableHeader>

              <SortableHeader sortKey="eldDriverFirstName" sortData={sortData} align="right">
                Last Used Driver
              </SortableHeader>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredList.length === 0 && <TableRow>
              <TableCell rowSpan={4}>
                <NoneFoundReport padding={0} />
              </TableCell>
              </TableRow>}
            {filteredList.map((line, index) =>
            (<TableRow key={index}>
              <TruxTableCell>
                {line.trailer}
              </TruxTableCell>

              <TruxTableCell>
                {line.vehicleUnitNumber}
              </TruxTableCell>

              <TruxTableCell align="center">
                {formatDateForPreview(line.latestUseDate)}
              </TruxTableCell>

              <TruxTableCell align="right">
                {line.eldDriverFirstName} {line.eldDriverLastName}
              </TruxTableCell>
            </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});

type PageModel = {
  trailersPerTruckLines?: TrailersPerTruckLine[],
  organizations: Organization[],
}

export default function Page() {
  const settings = useSettingsContext();
  const dataLoader = useCallback(() => ({
    trailersPerTruckLines: axios.get(endpoints.trailersPerTruckLines.findAll).then(response => response.data),
    organizations: axios.get(endpoints.eldOrganizations.childOrganizations).then(response => response.data),
  }), []);
  const {
    data,
    updateData,
    setData,
    error,
    loading,
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      console.log(error);
      alert(error);
    }
  }, [loading, error]);


  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        {data && data?.trailersPerTruckLines?.length !== 0 &&
          <PageHeader title="Trailer Assignments" />
        }

        {data && data?.trailersPerTruckLines?.length === 0 &&
          <NoneFound title="No Trailer Assignments Found" message="Drivers will add Trailer assignments while using the ELD app." />
        }

        {data && data?.trailersPerTruckLines?.length !== 0 && <TrailersPerTruckLineList trailersPerTruckLines={data.trailersPerTruckLines} />}
      </DisplayPageData>
    </Container>
  );
}
